import { getAuthHeaders } from "../AuthHeader/getAuthHeader"
import api from "../Interceptors"
import { ApiError } from "../ApiError"

interface ProjectPayload {
    name: string;
    summary: string;
    companyName: string;
    projectThumbnail: string;
    status: string;
    userId?: string;
    location: string;
    uploadControlPanelDoc?: string | File;
}
export interface GetAllProjectsParams {
    page: number;
    limit: number;
    search?: string;
    sortOrder?: "ASC" | "DESC";
}
/** Map API project (assignedUserId) to app shape (userId) so we keep sending userId and use userId everywhere in the app. */
interface Project {
    userId?: string;
    assignedUserId?: string;
}

type IfcJobApiResponse = {
    job_id?: string;
    status?: string | null;
    output?: string | null;
    output_url?: string | null;
    reportUrl?: string | null;
};

const mapIfcJobResponse = (data: IfcJobApiResponse | null | undefined) => ({
    job_id: data?.job_id ?? "",
    status: data?.status ?? null,
    output: data?.output ?? data?.output_url ?? data?.reportUrl ?? null,
});

function mapProjectFromApi<T extends Project>(project: T | null | undefined): T | null | undefined {
    if (!project) return project;

    return {
        ...project,
        userId: project.assignedUserId ?? project.userId,
    };
}

export const createProject = async (payload: ProjectPayload) => {
    try {
        const header = await getAuthHeaders()
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...header,
            }
        }
        const res = await api.post("/projects", payload, config)
        return res.data

    } catch (error) {
        throw new ApiError(error)
    }
}

export const getProjectById = async (id: string) => {
    try {
        const header = await getAuthHeaders();
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...header,
            },
        };
        const res = await api.get(`/projects/${id}`, config);
        return mapProjectFromApi(res.data);
    } catch (error) {
        throw new ApiError(error);
    }
};

/** IFC file type for upload-url request. */
const IFC_FILE_TYPE = "application/ifc";

/**
 * Upload IFC file: 1) get presigned URL and upload to S3, 2) register job with backend (POST /upload-ifc).
 * Backend creates DB row (status: queued) and pushes to SQS; worker updates status in DB.
 */
export const uploadIFC = async (file: File) => {
    try {
        const header = await getAuthHeaders();
        const uploadUrlRes = await api.post(
            "/upload-url",
            {
                files: [{ filename: file.name, fileType: IFC_FILE_TYPE, fileSize: file.size }],
                type: "ifcModels",
            },
            { headers: { "Content-Type": "application/json", ...header } }
        );
        const files = uploadUrlRes.data?.files;
        const signed = Array.isArray(files) ? files[0] : null;
        if (!signed?.uploadUrl || !signed?.key) throw new Error("Failed to get upload URL");

        await api.put(signed.uploadUrl, file, {
            headers: { "Content-Type": IFC_FILE_TYPE },
        });

        const { data } = await api.post(
            "/upload-ifc",
            { s3_key: signed.key },
            { headers: { "Content-Type": "application/json", ...header } }
        );
        return mapIfcJobResponse(data);
    } catch (error) {
        throw error;
    }
};

export const getJobStatus = async (jobId: string) => {
    try {
        const header = await getAuthHeaders();
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...header,
            },
        };
        const { data } = await api.get(`/job/${jobId}`, config);
        return mapIfcJobResponse(data);
    } catch (error) {
        throw error;
    }
};

export const getAllProjects = async (
    page: number = 1,
    limit: number = 10,
    search?: string,
    sortOrder?: "ASC" | "DESC",
) => {
    try {
        const header = await getAuthHeaders()
        const params: GetAllProjectsParams = {
            page,
            limit,
        };
        const trimmed = (search ?? "").trim();
        if (trimmed) {
            params.search = trimmed.substring(0, 256); // Cap length; backend should also enforce query limits
        }
        if (sortOrder) {
            params.sortOrder = sortOrder;
        }
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...header,
            },
            params,
        };
        const res = await api.get("/projects", config);
        const data = res.data || {};
        const projects = (data.projects || []).map(mapProjectFromApi);
        return { ...data, projects };

    } catch (error) {
        throw new ApiError(error)
    }
}

export const updateProject = async (id: string, payload: ProjectPayload) => {
    try {
        const header = await getAuthHeaders()
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...header,
            }
        }
        const { data } = await api.put(`/projects/${id}`, payload, config);
        return data

    } catch (error) {
        throw new ApiError(error)
    }
}

/** PUT /projects/files/:projectId - body: { ifcModel: string | string[] } - link uploaded IFC key(s) to project */
export const updateProjectFiles = async (
    projectId: string,
    payload: { ifcModel: string | string[] },
) => {
    try {
        const header = await getAuthHeaders();
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...header,
            },
        };
        const { data } = await api.put(`/projects/files/${projectId}`, payload, config);
        return data;
    } catch (error) {
        throw new ApiError(error);
    }
};

/** POST /projects/ifc-processing/:projectId - no body, starts IFC processing for the project */
export const startIfcProcessing = async (projectId: string) => {
    try {
        const header = await getAuthHeaders();
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...header,
            },
        };
        const { data } = await api.post(`/projects/ifc-processing/${projectId}`, {}, config);
        return data;
    } catch (error) {
        throw new ApiError(error);
    }
};

/** PATCH /projects/status/:projectId - body: { status: string } (e.g. "accepted-for-processing") */
export const updateProjectStatus = async (projectId: string, status: string) => {
    try {
        const header = await getAuthHeaders();
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...header,
            },
        };
        const { data } = await api.put(`/projects/status/${projectId}`, { status }, config);
        return data;
    } catch (error) {
        throw new ApiError(error);
    }
};

/** GET /projects/ifc-reports/:id – returns array of { ifcModelReportPreview, name }. */
export const getProjectIFCModelReport = async (id: string) => {
    try {
        const header = await getAuthHeaders();
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...header,
            },
            // IFC report generation is async; "not ready" should not toast as an error.
            suppressToast: true,
        };
        const res = await api.get(`/projects/ifc-reports/${id}`, config);
        const data = res.data;
        if (Array.isArray(data)) return data;
        if (data && typeof data === "object" && Array.isArray((data as { reports?: unknown }).reports))
            return (data as { reports: unknown[] }).reports;
        return [];
    } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        // Treat "not ready / not found yet" as empty list to keep the UI quiet.
        if (status === 404 || status === 409 || status === 202 || status === 425) return [];
        throw new ApiError(error);
    }
};

export const deleteProjects = async (id: string) => {
    try {
        const header = await getAuthHeaders()
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...header,
            }
        }
        const { data } = await api.delete(`/projects/${id}`, config);
        return data

    } catch (error) {
        throw new ApiError(error)
    }
}